// Condensed unit-creation wizard. Reached from the property page's "+ Egység"
// tile (route /properties/:id/units/new). Mirrors the property-creation wizard
// (NewPropertyView) but scoped to a single unit and without the property-level
// steps or server-side draft persistence.
//
// The unit is created early — on leaving the Basics step — because the
// owners / documents / meters panels save directly against an existing
// unit id (same trick the property wizard uses with the first unit). From
// there each form step does a full PUT (the backend UPDATE is a full row
// replace, so we always send the accumulated draft).
//
// Steps are type-aware: apartments/commercial get the full flow, simpler unit
// types (parking, garage, storage…) skip the rental/energy + parties steps.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { stepper } from '../components/Stepper.js';
import { showFormError, clearFormError } from '../core/formError.js';
import {
  emptyUnitDraft, unitBasicsFieldsHtml, unitTermsFieldsHtml, unitAddressSectionHtml,
  unitPayloadFromDraft, readUnitFormIntoDraft, wireUnitFormApartmentOnly,
  wireUnitCurrencySuffix,
} from '../components/UnitCard.js';
import {
  wireFurnishedRadio, wireEnergyRatingInputs, wireEnergyCertVerify, energySectionHtml,
  wireAddressFields, amenitiesSectionHtml, wireAmenitiesChips,
} from '../components/PropertyBasicsFields.js';
import { attachUnitMetersPanel } from '../components/UnitMetersPanel.js';
import { attachUnitDocumentsPanel } from '../components/UnitDocumentsPanel.js';
import { attachUnitExternalOwnersPanel } from '../components/UnitExternalOwnersPanel.js';

const FULL_FLOW_TYPES = ['apartment', 'house', 'commercial'];

export class NewUnitWizardView extends Component {
  constructor({ params } = { params: {} }) {
    super();
    this.propertyId = params.id;
    this.state = {
      step: 'basics',
      draft: emptyUnitDraft(),
      unitId: null,
      property: null,
      loading: true,
      busy: false,
      error: null,
    };
    this._panels = [];
  }

  get _propHref() { return `#/properties/${encodeURIComponent(this.propertyId)}`; }
  get _currency() { return (this.state.property?.currency || 'HUF').toUpperCase(); }
  get _canManage() {
    const p = this.state.property;
    return !!(p && auth.user && p.owner_user_id === auth.user.id);
  }

  // Type-aware step list. Apartments/commercial get the full flow; simpler
  // unit types skip rental/energy (details) and documents/owners (parties).
  _steps() {
    const full = FULL_FLOW_TYPES.includes(this.state.draft.type);
    const keys = ['basics'];
    if (full) keys.push('details', 'parties');
    keys.push('meters', 'done');
    return keys.map((key) => ({ key, label: t('unit.wizard.steps.' + key) || key }));
  }

  template() {
    return `
      <section class="stack">
        <a href="${this._propHref}" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('unit.back') || 'Vissza az ingatlanhoz')}</span></a>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this.state.loading ? `<div class="muted">${esc(t('property.loading') || 'Betöltés…')}</div>` : `
          <div class="panel">
            <div class="panel-head">
              <div class="panel-num">${icon(unitIcon(this.state.draft.type), {})}</div>
              <div class="panel-titles">
                <div class="panel-title-eyebrow">${esc(t('property.units_title') || 'Egység')}</div>
                <h2 class="panel-title">${esc(t('unit.new_title') || 'Új egység')}</h2>
              </div>
            </div>
            <div class="panel-body">
              ${stepper(this._steps(), this._currentIndex() + 1)}
              <div class="wizard-step" data-wizard-step="${esc(this.state.step)}">
                ${this._stepContentHtml()}
              </div>
              ${this._navHtml()}
            </div>
          </div>
        `}
      </section>
    `;
  }

  _currentIndex() {
    const steps = this._steps();
    const i = steps.findIndex((s) => s.key === this.state.step);
    return i < 0 ? 0 : i;
  }

  _stepContentHtml() {
    const d = this.state.draft;
    const cur = this._currency;
    switch (this.state.step) {
      case 'details':
        return `
          <form class="unit-page-form" data-unit-form autocomplete="off">
            <div data-form-error></div>
            ${unitTermsFieldsHtml(d, cur)}
            ${amenitiesSectionHtml(d.amenities)}
            <div class="eyebrow" style="margin: 14px 0 6px;">${esc(t('property.wizard.steps.energy') || 'Energetika')}</div>
            ${energySectionHtml({
              energy_rating: d.energy_rating,
              energy_certificate_number: d.energy_certificate_number,
              energy_certificate_expires_at: d.energy_certificate_expires_at,
            }, { showDropzone: false, showHelp: false })}
          </form>`;
      case 'parties':
        return this.state.unitId ? `
          <p class="panel-lede">${esc(t('unit.wizard.parties_lede') || 'Tulajdoni lap és tulajdonosok — opcionális, később is megadható.')}</p>
          <div class="stack" style="margin-top:12px;">
            <div data-unit-documents-slot="${esc(this.state.unitId)}"></div>
            <div data-unit-external-owners-slot="${esc(this.state.unitId)}"></div>
          </div>` : '';
      case 'meters':
        return this.state.unitId ? `
          <p class="panel-lede">${esc(t('unit.wizard.meters_lede') || 'Mérők — opcionális, később is hozzáadható.')}</p>
          <div class="stack" style="margin-top:12px;">
            <div data-unit-meters-slot="${esc(this.state.unitId)}"></div>
          </div>` : '';
      case 'done':
        return `
          <div class="stack-2" style="text-align:center; padding:18px 0;">
            <span class="badge success" style="align-self:center;"><span class="dot-anim"></span>${esc(t('unit.wizard.done_recorded') || 'Létrehozva')}</span>
            <p class="panel-lede">${esc(t('unit.wizard.done_body') || 'Az egység létrejött. Megnyithatod a részletekhez, vagy visszamehetsz az ingatlanhoz.')}</p>
            <div class="row" style="gap:var(--space-3); justify-content:center;">
              <a class="btn btn-primary" href="${this._propHref}/units/${encodeURIComponent(this.state.unitId || '')}">
                <span>${esc(t('unit.wizard.open') || 'Egység megnyitása')}</span>${icon('arrow-right', { size: 'sm' })}
              </a>
              <a class="btn btn-ghost" href="${this._propHref}">${esc(t('unit.back') || 'Vissza az ingatlanhoz')}</a>
            </div>
          </div>`;
      case 'basics':
      default:
        return `
          <form class="unit-page-form" data-unit-form autocomplete="off">
            <div data-form-error></div>
            ${unitBasicsFieldsHtml(d, cur)}
            ${unitAddressSectionHtml(d)}
          </form>`;
    }
  }

  _navHtml() {
    if (this.state.step === 'done') return '';
    const steps = this._steps();
    const i = this._currentIndex();
    const isFirst = i <= 0;
    const nextKey = steps[i + 1]?.key;
    const nextLabel = nextKey === 'done'
      ? (t('unit.wizard.finish') || 'Befejezés')
      : (t('property.wizard.nav.next') || 'Tovább');
    // The management-only steps (parties, meters) are optional, so give them
    // a Skip affordance that advances without saving — mirrors the property
    // wizard. (basics/details save on Next, so they don't get one.)
    const canSkip = this.state.step === 'parties' || this.state.step === 'meters';
    return `
      <div class="wizard-nav" style="display:flex; gap:10px; margin-top:24px; padding-top:20px; border-top:1px solid var(--color-border);">
        <button type="button" class="btn btn-ghost" data-action="wizard-back" ${isFirst ? 'disabled' : ''}>
          ${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.wizard.nav.back') || 'Vissza')}</span>
        </button>
        <span style="flex:1;"></span>
        ${canSkip ? `
          <button type="button" class="btn btn-ghost" data-action="wizard-skip">
            <span>${esc(t('property.wizard.nav.skip') || 'Kihagyás')}</span>
          </button>` : ''}
        <button type="button" class="btn btn-primary" data-action="wizard-next" ${this.state.busy ? 'disabled' : ''}>
          <span>${esc(nextLabel)}</span>${icon('arrow-right', { size: 'sm' })}
        </button>
      </div>`;
  }

  mount() {
    const on = (sel, evt, fn) => this.el.querySelectorAll(sel).forEach((el) => el.addEventListener(evt, fn));

    if (this.state.loading && this.state.property === null && !this._loadStarted) {
      this._loadStarted = true;
      this._loadProperty();
    }

    on('[data-action="wizard-next"]', 'click', () => this._next());
    on('[data-action="wizard-back"]', 'click', () => this._back());
    on('[data-action="wizard-skip"]', 'click', () => this._skip());

    const form = this.el.querySelector('[data-unit-form]');
    if (form) {
      if (this.state.step === 'basics') {
        wireUnitFormApartmentOnly(form);
        // Optional unit address override: blank country stays blank
        // (= inherit from the parent property) — that's the point of
        // requireDefault:false. Same datalist + postal-code logic the
        // property form uses.
        wireAddressFields(form, { requireDefault: false });
        // Currency override picker: live-syncs the (HUF) label suffix
        // on the rent/közös inputs. Inherit fallback comes from the
        // already-loaded parent property.
        wireUnitCurrencySuffix(form, this.state.property?.currency || 'HUF');
        // Changing the unit type can change the step flow (full vs. short),
        // so mirror it into state to refresh the stepper — preserving the
        // already-typed values via the draft.
        const typeSel = form.querySelector('[name="type"]');
        if (typeSel) typeSel.addEventListener('change', () => {
          this.state.draft = readUnitFormIntoDraft(form, this.state.draft);
          this.setState({});
        });
      }
      if (this.state.step === 'details') {
        wireFurnishedRadio(form);
        // Amenity chips read straight off the DOM in readUnitFormIntoDraft
        // (the .chip-on class), so wiring the toggle is all that's needed
        // for the selection to flow into the draft + payload.
        wireAmenitiesChips(form, { initial: Array.isArray(this.state.draft.amenities) ? this.state.draft.amenities : [] });
        wireEnergyRatingInputs(form);
        wireEnergyCertVerify(form, {
          onMatch: (row) => {
            if (row?.expirationDate) {
              const inp = form.querySelector('[name="energy_certificate_expires_at"]');
              if (inp && !inp.value) inp.value = String(row.expirationDate).slice(0, 10);
            }
          },
        });
      }
    }

    // Panel steps mount their per-unit panels against the freshly-created unit.
    this._destroyPanels();
    if (this.state.unitId && this._canManage) {
      const uid = this.state.unitId;
      if (this.state.step === 'parties') {
        this._panels = [
          attachUnitDocumentsPanel(this.el, { unitId: uid, canManage: true }),
          attachUnitExternalOwnersPanel(this.el, { unitId: uid, canManage: true }),
        ].filter(Boolean);
      } else if (this.state.step === 'meters') {
        this._panels = [attachUnitMetersPanel(this.el, { unitId: uid, canManage: true })].filter(Boolean);
      }
    }
  }

  async _loadProperty() {
    try {
      const property = await api.get(`/properties/${this.propertyId}`);
      this.setState({ property, loading: false });
    } catch (err) {
      this.setState({ loading: false, error: err instanceof ApiError ? err.message : (t('property.errors.load') || 'Betöltés sikertelen') });
    }
  }

  // Read the visible form back into the draft (no-op on panel steps).
  _syncDraft() {
    const form = this.el.querySelector('[data-unit-form]');
    if (form) this.state.draft = readUnitFormIntoDraft(form, this.state.draft);
  }

  async _next() {
    if (this.state.busy) return;
    const curKey = this.state.step;

    // Form steps: sync + validate + persist (POST first time, PUT after).
    if (curKey === 'basics' || curKey === 'details') {
      const form = this.el.querySelector('[data-unit-form]');
      if (form) clearFormError(form);
      this._syncDraft();
      const payload = unitPayloadFromDraft(this.state.draft);
      if (payload._error === 'identifier') {
        if (form) showFormError(form, t('property.wizard.errors.unit_identifier_required') || t('property.unit_form.identifier'), { field: 'identifier' });
        return;
      }
      this.setState({ busy: true, error: null });
      try {
        if (!this.state.unitId) {
          const row = await api.post(`/properties/${this.propertyId}/units`, payload);
          this.state.unitId = row.id;
        } else {
          await api.put(`/units/${encodeURIComponent(this.state.unitId)}`, payload);
        }
      } catch (err) {
        this.setState({ busy: false });
        const form2 = this.el.querySelector('[data-unit-form]');
        if (form2) showFormError(form2, err instanceof ApiError ? err.message : (t('property.errors.save') || 'Mentés sikertelen'));
        return;
      }
      this.state.busy = false;
    }

    // Compute the next step AFTER the draft is synced — the type may have
    // changed the flow (e.g. apartment → parking drops the details/parties).
    const steps = this._steps();
    const i = steps.findIndex((s) => s.key === curKey);
    const next = steps[i + 1];
    this.setState({ busy: false, step: next ? next.key : curKey });
  }

  _back() {
    const steps = this._steps();
    const i = steps.findIndex((s) => s.key === this.state.step);
    if (i <= 0) { window.location.hash = this._propHref.slice(1); return; }
    this._syncDraft();
    this.setState({ step: steps[i - 1].key });
  }

  // Skip an optional management step (parties / meters) without saving —
  // those steps don't POST/PUT, so advancing is all there is to do.
  _skip() {
    const steps = this._steps();
    const i = steps.findIndex((s) => s.key === this.state.step);
    const next = steps[i + 1];
    if (next) this.setState({ step: next.key });
  }

  _destroyPanels() {
    for (const p of this._panels) { try { p?.destroy?.(); } catch {} }
    this._panels = [];
  }

  destroy() {
    this._destroyPanels();
    super.destroy();
  }
}
